import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEventCallback } from '@/hooks/useEventCallback';
import { useEventListener } from '@/hooks/useEventListener';
import { parseJSON } from '@/utils';

declare global {
  interface WindowEventMap {
    'eddy-custom-storage': CustomEvent;
  }
}

type SetValue<T> = Dispatch<SetStateAction<T>>;

export enum StorageType {
  Local = 'localStorage',
  Session = 'sessionStorage',
}

export function useBrowserStorage<T>(key: string, initialValue: T, storageType: StorageType): [T, SetValue<T>] {
  const storage = useMemo(() => (storageType === StorageType.Local ? localStorage : sessionStorage), [storageType]);

  const readValue = useCallback((): T => {
    try {
      const item = storage.getItem(key);
      return item ? (parseJSON(item) as T) : initialValue;
    } catch (error) {
      console.error(error);
      return initialValue;
    }
  }, [initialValue, key, storage]);

  const [storedValue, setStoredValue] = useState<T>(readValue);

  const setValue: SetValue<T> = useEventCallback((value) => {
    try {
      // Allow value to be a function so we have the same API as useState
      const newValue = value instanceof Function ? value(storedValue) : value;

      storage.setItem(key, JSON.stringify(newValue));
      setStoredValue(newValue);

      // dispatch a custom event to notify other hooks in the same tab
      window.dispatchEvent(new CustomEvent('eddy-custom-storage', { detail: { key } }));
    } catch (error) {
      console.error(error);
    }
  });

  // Capture the initial reader; the effect runs once on mount to sync from storage.
  const readValueRef = useRef(readValue);

  useEffect(() => {
    setStoredValue(readValueRef.current());
  }, []);

  const handleStorageChange = useCallback(
    (event: StorageEvent | CustomEvent) => {
      if (isStorageEvent(event) && event.key !== key) {
        return;
      }
      setStoredValue(readValue());
    },
    [key, readValue]
  );

  // for other browser tabs
  useEventListener('storage', handleStorageChange);

  // for other hooks on this tab
  useEventListener('eddy-custom-storage', handleStorageChange);

  return [storedValue, setValue];
}

function isStorageEvent(event: StorageEvent | CustomEvent): event is StorageEvent {
  return event.type === 'storage';
}
